<template>
  <div class="organism product-grid-picker sm:py-20 py-8 px-8" :class="organism.removeMargin">
    <!-- Picked products are split by post type into the same three tabs the
         neighborhood detail page uses (Model Homes / Floorplans / Quick Move-In).
         A tab only renders when the editor picked at least one of that type.
         title1/description are handed to TabbedContent rather than rendered
         here, so they sit inside its gradient header above the tab nav. -->
    <OrganismsTabbedContent
      v-if="tabbedContent.item.length"
      :organism="tabbedContent"
    >
      <template v-for="tab in tabbedContent.item" #[tab.id]>
        <div
          :class="[
            'items-self justify-center text-gray-800 flex flex-wrap gap-8 pt-10 pb-12 mx-auto max-w-screen-xl'
          ]"
        >
          <!-- Iterate over the picked products belonging to this tab -->
          <div v-for="product in productsByTab[tab.id]" :key="product.id" class="product-item z-0 w-full max-w-sm mb-2 text-center item">
            <NuxtLink :to="product.uri">
              <h3 class="pb-2 text-2xl font-semibold font-archer">
                {{ product.title }}
              </h3>
              <div
                class="border border-neutral-400 drop-shadow-lg hover:drop-shadow-md"
              >
                <figure class="relative w-full h-56">
                  <img
                  :src="getImageSource(product)"
                  class="object-cover w-full h-full"
                  />
                  <span
                    v-if="getMsg(product)"
                    class="absolute left-0 px-4 py-[5px] text-white uppercase top-6 font-ubuntu bg-snipe-blue"
                  >
                    {{ getMsg(product) }}
                  </span>
                  <span
                    v-if="getBasePrice(product)"
                    :class="[
                      'absolute right-0 px-4 py-[5px] text-white uppercase top-6 font-ubuntu bg-bc-purple'
                    ]"
                    v-html="getBasePrice(product)"
                  >
                  </span>
                </figure>
                <div
                  class="flex px-2 pt-3 pb-4 bg-white justify-evenly details gap-4 empty:p-0"
                >
                  <div v-if="getBeds(product)" class="flex flex-col">
                    <span class="text-[22px]">
                      {{ getBeds(product) }}
                    </span>
                    <span class="text-xs font-bold font-arial">BEDS</span>
                  </div>
                  <div v-if="getBaths(product)" class="flex flex-col">
                    <span class="text-[22px]">
                      {{ getBaths(product) }}
                    </span>
                    <span class="text-xs font-bold font-arial">BATH</span>
                  </div>
                  <div v-if="getSqFt(product)" class="flex flex-col">
                    <span class="text-[22px]">
                      {{ getSqFt(product) }}
                    </span>
                    <span class="text-xs font-bold font-arial">SQ. FT.</span>
                  </div>
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </template>
    </OrganismsTabbedContent>

    <!-- Nothing picked yet: keep the heading visible on its own. -->
    <template v-else>
      <h1 class="text-center text-3xl font-semibold md:text-5xl title-1 font-archer text-[#1a2937]">{{ gridItems?.title1 }}</h1>
      <div class="text-center font-arial text-lg text-[#6b7280] pt-5 pb-10 max-w-[820px] m-auto" v-html="gridItems.description"></div>
    </template>

    <!-- Only set by pages/rebuilding-altadena-ca.vue (post 21197). -->
    <AtomsAltadenaDisclaimer v-if="organism.showAltadenaDisclaimer" class="pb-16" />
</div>
</template>

<style>
.product-grid-picker {
  /* The header carries the title, the description and the tab nav. It needs the
     gradient both for the heading treatment and because the tab nav is dark
     blue with a *white* active tab, which would vanish against a white page.
     The 100vw breakout mirrors .container-full-width (assets/css/base/theme.css)
     so the gradient spans the viewport even though the organism itself sits
     inside the page container. */
  .tabbed-content {
    & > header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      width: 100vw;
      margin-inline-start: 50%;
      margin-inline-end: 0;
      transform: translateX(-50%);
    }

    .header-content {
      @apply max-w-[820px] pb-10;

      .title1 {
        @apply md:text-5xl;
      }

      .description {
        @apply text-lg font-arial;
      }
    }

    .tabs-component-tabs {
      @apply max-w-[556px] mx-auto justify-center;
    }

    /* The panels sit inside the same header, so they break out to 100vw too.
       That is what keeps the white panel background covering the gradient
       edge to edge; the grid inside is what gets re-constrained. */
    .tabs-component-panels {
      @apply mb-0;
    }
  }
}
</style>

<script setup>
const props = defineProps({
  organism: Object,
});

const gridItems = ref(props.organism);
//console.log(gridItems.value.title1);

const productPicker = computed(() => gridItems.value.productPicker);
//console.log(productPicker.value);

// Tab order and labels mirror pages/homes/[location]/[community]/index.vue.
const TABS = [
  { id: "model-homes", name: "Model Homes" },
  { id: "floorplans", name: "Floorplans" },
  { id: "quick-move-in", name: "Quick Move-In" },
];

// Same fallback order the neighborhood detail page uses for its default tab.
const DEFAULT_TAB_PRIORITY = ["floorplans", "quick-move-in", "model-homes"];

// `productPicker` is a single ACF post_object field spanning three post types,
// so the tab a product belongs to comes from its GraphQL type. The ACF group
// check is a fallback for anything returned without a __typename.
function getProductTab(product) {
  switch (product?.__typename) {
    case "Model":
      return "model-homes";
    case "Plan":
      return "floorplans";
    case "Home":
      return "quick-move-in";
  }

  if (product?.modelDetails) return "model-homes";
  if (product?.floorplanDetails) return "floorplans";
  if (product?.qmiHomeDetails) return "quick-move-in";

  return null;
}

const productsByTab = computed(() => {
  const grouped = {
    "model-homes": [],
    floorplans: [],
    "quick-move-in": [],
  };

  if (Array.isArray(productPicker.value)) {
    productPicker.value.forEach((product) => {
      const tab = getProductTab(product);
      if (tab) {
        grouped[tab].push(product);
      }
    });
  }

  return grouped;
});

// TabbedContent's header renders title1 as the <h2>, title2 as the <h3>
// subtitle and description as the copy below them (TabbedContent.vue).
// Blank out whitespace-only values so its v-if guards don't render an empty
// heading. NOTE: ACF only exposes title_1 and description on the
// product-grid-picker layout - title_2's conditional logic has to include
// "product-grid-picker" before the <h3> can be populated from the CMS.
const headerText = computed(() => {
  const clean = (value) =>
    typeof value === "string" && value.trim() ? value.trim() : null;

  return {
    title1: clean(gridItems.value.title1),
    title2: clean(gridItems.value.title2),
    description: clean(gridItems.value.description),
  };
});

const tabbedContent = computed(() => {
  const items = TABS.filter((tab) => productsByTab.value[tab.id].length);
  const ids = items.map((item) => item.id);

  return {
    containerWidth: gridItems.value.containerWidth ?? "default",
    title1: headerText.value.title1,
    title2: headerText.value.title2,
    description: headerText.value.description,
    item: items,
    defaultTabHash: DEFAULT_TAB_PRIORITY.find((id) => ids.includes(id)) ?? "",
    loadingLabel: "Loading homes…",
  };
});

function getBasePrice(product) {
  let price;
  let prefix = '';

  if (product?.floorplanDetails) {
    prefix = '<i>From</i> $';
    price = product.floorplanDetails.basePrice;
  } else if (product?.modelDetails) {
    prefix = '<i>From</i> $';
    price = product.modelDetails.floorplan.floorplanDetails.basePrice;
  } else if (product?.qmiHomeDetails) {
    prefix = '$';
    price = product.qmiHomeDetails.price;
  }

  // Return null or a default value if none of the conditions are met
  if (price === null || price === undefined) {
    return null;
  }

  // Format the price with commas
  const formattedPrice = new Intl.NumberFormat('en-US').format(price);

  return `${prefix}${formattedPrice}`;
}

function getImageSource(product) {
  if (product.floorplanDetails?.elevations?.length > 0) {
    return product.floorplanDetails.elevations[0].sourceUrl;
  }
  else if (product.featuredImage?.node) {
    return product.featuredImage.node.sourceUrl;
  }

  // Return null or a default value if none of the conditions are met
  return null;
}

function getBeds(product) {
  if (product?.floorplanDetails) {
    // Assuming detailMinMax can handle single values as well
    return detailMinMax(product.floorplanDetails.bedroomsMin, product.floorplanDetails.bedroomsMax);
  } else if (product?.modelDetails) {
    return detailMinMax(
      product.modelDetails.floorplan.floorplanDetails.bedroomsMin,
      product.modelDetails.floorplan.floorplanDetails.bedroomsMax
    );
  } else if (product?.qmiHomeDetails) {
    // Assuming qmiHomeDetails only has a single value for bedrooms
    return product.qmiHomeDetails.bedrooms;
  }

  // Return null or a default value if none of the conditions are met
  return null;
}

function getBaths(product) {
  if (product?.floorplanDetails) {
    // Assuming detailMinMax can handle single values as well
    return detailMinMax(product.floorplanDetails.bathroomsMin, product.floorplanDetails.bathroomsMax);
  } else if (product?.modelDetails) {
    return detailMinMax(
      product.modelDetails.floorplan.floorplanDetails.bathroomsMin,
      product.modelDetails.floorplan.floorplanDetails.bathroomsMax
    );
  } else if (product?.qmiHomeDetails) {
    // Assuming qmiHomeDetails only has a single value for bathrooms
    return product.qmiHomeDetails.bathrooms;
  }

  // Return null or a default value if none of the conditions are met
  return null;
}

function getSqFt(product) {
  if (product?.floorplanDetails) {
    return product?.floorplanDetails?.finishedSqrFt || product?.floorplanDetails?.mainSqrFt;
  } else if (product?.modelDetails) {
    return product?.modelDetails?.floorplan?.floorplanDetails?.finishedSqrFt || product?.modelDetails?.floorplan?.floorplanDetails?.mainSqrFt;
  } else if (product?.qmiHomeDetails) {
    return product?.qmiHomeDetails?.finishedSqrFt || product?.qmiHomeDetails?.mainSqrFt;
  }

  // Return null or a default value if none of the conditions are met
  return null;
}

function getMsg(product) {
  const statusMessage = product?.productStatus?.statusMessage;
  const statusCustom = product?.productStatus?.statusCustom;

  if (Array.isArray(statusMessage) && statusMessage[1] === "Custom Message") {
    return statusCustom;
  }

  return statusMessage ? statusMessage[1] : null;
}

</script>
